import { Component } from 'react';
import PropTypes from 'prop-types';
import I18n from 'I18n';
import UISelect from 'UIComponents/input/UISelect';
import UsersAndTeamsSearchOptionComponent from './UsersAndTeamsSearchOptionComponent';
import { isTeamId, isUserId, makeTeamIdForSelect, makeUserIdForSelect, extractTeamIdFromSelect, extractUserIdFromSelect } from './UsersAndTeamsSearchUtils';
import { getExplicitlySelectedUserIds } from '../../utils/TeamUtils';
import { getSortedUsers } from '../../utils/UserUtils';
import { makeUserSelectOption } from './UserSelectOption';
import { makeFlatListOfTeamSelectOptions } from './TeamSelectOptions';
import { UserOptionType, TeamOptionType } from '../../types/OptionTypes';
import { jsx as _jsx } from "react/jsx-runtime";
// Takes a list of IDs [team-1, user-2] ect. and
// returns two seperate lists with unique key for users and teams
const getSelectedUserAndTeamIdsFromCombinedList = values => {
  const result = values.reduce((acc, id) => {
    if (isTeamId(id)) {
      acc.selectedTeamIds.push(extractTeamIdFromSelect(id));
    } else if (isUserId(id)) {
      acc.selectedUserIds.push(extractUserIdFromSelect(id));
    }
    return acc;
  }, {
    selectedUserIds: [],
    selectedTeamIds: []
  });
  return result;
};

// Takes three lists of ID's, users, teams and users from selected teams, and creates one list
// of ID's with a unique prefix in front of users/teams to distinguish
// which type of ID the selected option is referring to.
const makeCombinedUserAndTeamIdsList = ({
  selectedUserIds,
  selectedTeamIds,
  userIdsForSelectedTeamIds
}) => {
  return selectedUserIds.map(id => makeUserIdForSelect(id)).concat(selectedTeamIds.map(id => makeTeamIdForSelect(id))).concat(userIdsForSelectedTeamIds.map(id => makeUserIdForSelect(id)));
};
const makeUserAndTeamGroupedSelectOptions = (users, teams, userIdsForSelectedTeamIds, disabledUsers, allowedTeamIds) => {
  const sortedUsers = getSortedUsers(users);
  const userSelectOptions = sortedUsers.map(user => makeUserSelectOption(user, userIdsForSelectedTeamIds, disabledUsers));
  const teamSelectOptions = makeFlatListOfTeamSelectOptions({
    teams,
    allowedTeamIds
  });
  return [{
    text: I18n.text('ui-asset-management-lib.labels.teams'),
    options: teamSelectOptions
  }, {
    text: I18n.text('ui-asset-management-lib.labels.users'),
    options: userSelectOptions
  }];
};

// For two identical lists, with one unique value, find the unique value between the two
const getUniqueId = (listOne, listTwo) => {
  if (listOne.length > listTwo.length) {
    return listOne.find(id => !listTwo.includes(id));
  }
  return listTwo.find(id => !listOne.includes(id));
};
class UsersAndTeamsSearch extends Component {
  constructor(...args) {
    super(...args);
    this.handleChange = ({
      target: {
        value
      }
    }) => {
      const newSelectedOptions = getSelectedUserAndTeamIdsFromCombinedList(value);
      const {
        selectedTeamIds,
        selectedUserIds,
        onAddTeam,
        onRemoveTeam,
        onAddUser,
        onRemoveUser,
        teams
      } = this.props;
      const newSelectedUserIds = getExplicitlySelectedUserIds(newSelectedOptions.selectedUserIds, selectedTeamIds, teams);
      const oldSelectedUserIds = getExplicitlySelectedUserIds(selectedUserIds, selectedTeamIds, teams);
      const didAddTeam = newSelectedOptions.selectedTeamIds.length > selectedTeamIds.length;
      const didRemoveTeam = newSelectedOptions.selectedTeamIds.length < selectedTeamIds.length;
      const didAddUser = newSelectedUserIds.length > oldSelectedUserIds.length;
      const didRemoveUser = newSelectedUserIds.length < oldSelectedUserIds.length;
      if (didAddTeam) {
        onAddTeam(getUniqueId(newSelectedOptions.selectedTeamIds, selectedTeamIds));
      } else if (didRemoveTeam) {
        onRemoveTeam(getUniqueId(newSelectedOptions.selectedTeamIds, selectedTeamIds));
      } else if (didAddUser) {
        onAddUser(getUniqueId(newSelectedUserIds, oldSelectedUserIds));
      } else if (didRemoveUser) {
        onRemoveUser(getUniqueId(newSelectedUserIds, oldSelectedUserIds));
      }
    };
    this.renderTeamOption = data => {
      const {
        disallowedUserTooltip
      } = this.props;
      return /*#__PURE__*/_jsx(UsersAndTeamsSearchOptionComponent, Object.assign({}, data, {
        DisallowedTeamFormattedMessage: this.props.DisallowedTeamFormattedMessage,
        disallowedUserTooltip: disallowedUserTooltip
      }));
    };
  }
  render() {
    const {
      users,
      teams,
      userIdsForSelectedTeamIds,
      selectedUserIds,
      selectedTeamIds,
      allowedTeamIds,
      disabledUsers = []
    } = this.props;
    const selectedUsersAndTeamIds = makeCombinedUserAndTeamIdsList({
      selectedUserIds,
      selectedTeamIds,
      userIdsForSelectedTeamIds
    });
    const usersAndTeamsOptions = makeUserAndTeamGroupedSelectOptions(users, teams, userIdsForSelectedTeamIds, disabledUsers, allowedTeamIds);
    return /*#__PURE__*/_jsx(UISelect, {
      "data-test-id": "users-and-team-search-input",
      multi: true,
      anchorType: "input",
      value: selectedUsersAndTeamIds,
      optionComponent: this.renderTeamOption,
      onChange: this.handleChange,
      options: usersAndTeamsOptions
    });
  }
}
UsersAndTeamsSearch.propTypes = {
  users: PropTypes.arrayOf(UserOptionType).isRequired,
  teams: PropTypes.arrayOf(TeamOptionType).isRequired,
  selectedUserIds: PropTypes.array.isRequired,
  selectedTeamIds: PropTypes.array.isRequired,
  userIdsForSelectedTeamIds: PropTypes.array.isRequired,
  onAddTeam: PropTypes.func.isRequired,
  onRemoveTeam: PropTypes.func.isRequired,
  onAddUser: PropTypes.func.isRequired,
  onRemoveUser: PropTypes.func.isRequired,
  allowedTeamIds: PropTypes.arrayOf(PropTypes.number),
  DisallowedTeamFormattedMessage: PropTypes.func,
  disabledUsers: PropTypes.arrayOf(PropTypes.number),
  disallowedUserTooltip: PropTypes.string
};
export default UsersAndTeamsSearch;